import React, { useState, useEffect, useCallback } from 'react';
import { useSessionState } from '../hooks/useSessionState';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { StatusBadge, PriorityBadge } from '../components/StatusBadge';
import { formatDate } from '../utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Search, 
  ClipboardList, 
  UserPlus, 
  Clock, 
  CheckCircle, 
  XCircle,
  Loader2,
  Eye,
  DollarSign,
  Upload,
  X,
  FileText,
  Image,
  List
} from 'lucide-react';
import { RequestDetailsSection } from '../components/RequestDetailsSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminQueuePage() {
  const navigate = useNavigate();
  const { getAuthHeader, isAdmin, employee } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [adminTeam, setAdminTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useSessionState('admin-queue:search', '');
  const [activeTab, setActiveTab] = useSessionState('admin-queue:activeTab', 'all');
  
  const role = employee?.role || 'Employee';
  const isManagement = role === 'Management';
  const isAdminHead = role === 'Admin Head' || role === 'Super Admin';
  const isAdminTeam = role === 'Admin Team';
  const canAssign = isAdminHead; // Admin Head and Super Admin can assign tasks
  const canChangeStatus = isAdminHead || isAdminTeam; // Both can change status (In Progress, Completed)
  const canDoAdminActions = isAdminHead || isAdminTeam; // Can perform admin actions (fetch admin team, etc.)
  const canAccess = ['Admin Team', 'Admin Head', 'Super Admin', 'Management'].includes(role);
  // Dialogs
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [assignDialog, setAssignDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  
  // Form states
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [statusRemarks, setStatusRemarks] = useState('');

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/tickets?admin_queue=true`, {
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        const data = await res.json();
        setTickets(data);
      }
    } catch (error) {
      console.error('Fetch tickets error:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const fetchAdminTeam = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/team-members`, {
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        const data = await res.json();
        setAdminTeam(data);
      }
    } catch (error) {
      console.error('Fetch admin team error:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (canAccess) {
      fetchTickets();
      if (canDoAdminActions) {
        fetchAdminTeam();
      }
    }
  }, [canAccess, canDoAdminActions, fetchTickets, fetchAdminTeam]);

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = 
      ticket.ticket_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.request_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.requester_name.toLowerCase().includes(searchQuery.toLowerCase());

    let matchesTab = true;
    if (activeTab === 'all') {
      matchesTab = true;
    } else if (activeTab === 'approved') {
      matchesTab = ticket.status === 'Approved';
    } else if (activeTab === 'in-process') {
      matchesTab = ticket.status === 'In Progress';
    } else if (activeTab === 'completed') {
      matchesTab = ticket.status === 'Completed';
    } else if (activeTab === 'rejected') {
      matchesTab = ticket.status === 'Rejected';
    } else if (activeTab === 'cancelled') {
      matchesTab = ticket.status === 'Cancelled';
    }

    return matchesSearch && matchesTab;
  });

  const handleAssign = async () => {
    if (!selectedTicket || !selectedAdmin) {
      toast.error('Please select an admin team member');
      return;
    }

    setActionLoading(true);
    try {
      const res = await fetch(`${API}/tickets/${selectedTicket.ticket_id}/assign?admin_id=${selectedAdmin}`, {
        method: 'PUT',
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        toast.success('Ticket assigned successfully');
        setAssignDialog(false);
        setSelectedTicket(null);
        setSelectedAdmin('');
        fetchTickets();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to assign ticket');
      }
    } catch (error) {
      toast.error('Failed to assign ticket');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedTicket || !newStatus) return;

    setActionLoading(true);
    try {
      const res = await fetch(`${API}/tickets/${selectedTicket.ticket_id}/status`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify({
          status: newStatus,
          approval_remarks: statusRemarks
        })
      });

      if (res.ok) {
        toast.success('Status updated successfully');
        setStatusDialog(false);
        setSelectedTicket(null);
        setNewStatus('');
        setStatusRemarks('');
        fetchTickets();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  if (!canAccess) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Access denied. Admin or Management privileges required.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-queue-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Admin Queue</h1>
          <p className="text-muted-foreground mt-1">
            {isManagement 
              ? 'View approved tickets and track progress'
              : 'Manage approved tickets and track progress'
            }
          </p>
        </div>

        {/* Search */}
        <div className="flex gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="admin-search-input"
            />
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">
              <List className="w-4 h-4 mr-2" />
              View All
            </TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved">
              <CheckCircle className="w-4 h-4 mr-2" />
              Approved
            </TabsTrigger>
            <TabsTrigger value="in-process" data-testid="tab-in-process">
              <Clock className="w-4 h-4 mr-2" />
              In Progress
            </TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">
              <CheckCircle className="w-4 h-4 mr-2" />
              Completed
            </TabsTrigger>
            {isAdminHead && (
              <TabsTrigger value="rejected" data-testid="tab-rejected">
                <XCircle className="w-4 h-4 mr-2" />
                Rejected
              </TabsTrigger>
            )}
            {isAdminHead && (
              <TabsTrigger value="cancelled" data-testid="tab-cancelled">
                <XCircle className="w-4 h-4 mr-2" />
                Cancelled
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5" />
                  {activeTab === 'all' ? 'All Tickets' :
                   activeTab === 'approved' ? 'Approved Tickets' : 
                   activeTab === 'in-process' ? 'In Progress' : 
                   activeTab === 'rejected' ? 'Rejected Tickets' :
                   activeTab === 'cancelled' ? 'Cancelled Tickets' : 'Completed Tickets'}
                </CardTitle>
                <CardDescription>
                  {filteredTickets.length} ticket{filteredTickets.length !== 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-6 space-y-3">
                    {[1, 2, 3].map(i => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : filteredTickets.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground empty-state">
                    <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">No tickets found</p>
                  </div>
                ) : (
                  <div className="overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead>Ticket ID</TableHead>
                          <TableHead>Request Type</TableHead>
                          <TableHead className="hidden md:table-cell">Requester</TableHead>
                          <TableHead className="hidden sm:table-cell">Priority</TableHead>
                          <TableHead className="hidden lg:table-cell">Assigned To</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredTickets.map((ticket) => (
                          <TableRow key={ticket.ticket_id} className="table-row-hover">
                            <TableCell>
                              <button
                                onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                                className="ticket-id-link font-mono text-sm"
                              >
                                {ticket.ticket_id}
                              </button>
                            </TableCell>
                            <TableCell className="font-medium max-w-[150px] truncate">
                              {ticket.request_type === 'Other' 
                                ? ticket.other_request_type 
                                : ticket.request_type}
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-muted-foreground">
                              {ticket.requester_name}
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              <PriorityBadge priority={ticket.priority} />
                            </TableCell>
                            <TableCell className="hidden lg:table-cell text-muted-foreground">
                              {ticket.assigned_admin_name || '-'}
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={ticket.status} />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                                  data-testid={`view-${ticket.ticket_id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                
                                {/* Admin Head only - Assign button */}
                                {isAdminHead && (
                                  <>
                                    {/* Assign button - disabled if already assigned */}
                                    {ticket.assigned_admin_name ? (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        disabled
                                        className="text-muted-foreground"
                                        title={`Assigned to ${ticket.assigned_admin_name}`}
                                        data-testid={`assigned-${ticket.ticket_id}`}
                                      >
                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                      </Button>
                                    ) : canAssign ? (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setSelectedTicket(ticket);
                                          setAssignDialog(true);
                                        }}
                                        data-testid={`assign-${ticket.ticket_id}`}
                                      >
                                        <UserPlus className="w-4 h-4" />
                                      </Button>
                                    ) : (
                                      <span className="text-xs text-muted-foreground">-</span>
                                    )}
                                  </>
                                )}
                                
                                {/* Status change - Admin Head and Admin Team can change */}
                                {canChangeStatus && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedTicket(ticket);
                                      setNewStatus(ticket.status);
                                      setStatusDialog(true);
                                    }}
                                    data-testid={`status-${ticket.ticket_id}`}
                                  >
                                    <Clock className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Assign Dialog */}
        <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Assign Ticket</DialogTitle>
              <DialogDescription>
                Assign {selectedTicket?.ticket_id} to an admin team member
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Ticket Summary */}
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium">{selectedTicket?.requester_name}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedTicket?.request_type} - {selectedTicket?.description?.substring(0, 100)}...
                </p>
              </div>
              
              {/* Request Details Section */}
              {selectedTicket && <RequestDetailsSection ticket={selectedTicket} compact />}
              
              <div className="space-y-2">
                <Label>Admin Team Member</Label>
                <Select value={selectedAdmin} onValueChange={setSelectedAdmin}>
                  <SelectTrigger data-testid="assign-admin-select">
                    <SelectValue placeholder="Select team member" />
                  </SelectTrigger>
                  <SelectContent>
                    {adminTeam.map((admin) => (
                      <SelectItem key={admin.employee_id} value={admin.employee_id}>
                        {admin.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAssign} 
                disabled={actionLoading}
                data-testid="confirm-assign-btn"
              >
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Assign
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Status Dialog */}
        <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Status</DialogTitle>
              <DialogDescription>
                Change the status of {selectedTicket?.ticket_id}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>New Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="new-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Remarks (Optional)</Label>
                <Textarea
                  value={statusRemarks}
                  onChange={(e) => setStatusRemarks(e.target.value)}
                  placeholder="Add any remarks..."
                  data-testid="status-remarks-textarea"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStatusDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleStatusUpdate} 
                disabled={actionLoading}
                data-testid="confirm-status-btn"
              >
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Update
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
